import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

export default function ImportData() {
  const { apiFetch, token } = useAuth();
  const [activeTab, setActiveTab] = useState('kassekladde');

  // Kassekladde state
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Members state
  const memberFileRef = useRef();
  const [memberFile, setMemberFile] = useState(null);
  const [memberPreview, setMemberPreview] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberImporting, setMemberImporting] = useState(false);
  const [memberResult, setMemberResult] = useState(null);
  const [memberError, setMemberError] = useState('');

  // Kassekladde handlers
  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    setPreview(null);
    setResult(null);
    setError('');
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const data = await apiFetch('/api/import/preview', { method: 'POST', body: formData, isFormData: true });
      setPreview(data);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const data = await apiFetch('/api/import/execute', { method: 'POST', body: formData, isFormData: true });
      setResult(data);
      setPreview(null);
    } catch (err) { setError(err.message); }
    setImporting(false);
  };

  // Member handlers
  const handleMemberFileChange = (e) => {
    const f = e.target.files[0];
    setMemberFile(f);
    setMemberPreview(null);
    setMemberResult(null);
    setMemberError('');
  };

  const handleMemberPreview = async () => {
    if (!memberFile) return;
    setMemberLoading(true);
    setMemberError('');
    const formData = new FormData();
    formData.append('file', memberFile);
    try {
      const data = await apiFetch('/api/import/members/preview', { method: 'POST', body: formData, isFormData: true });
      setMemberPreview(data);
    } catch (err) { setMemberError(err.message); }
    setMemberLoading(false);
  };

  const handleMemberImport = async () => {
    if (!memberFile) return;
    setMemberImporting(true);
    setMemberError('');
    const formData = new FormData();
    formData.append('file', memberFile);
    try {
      const data = await apiFetch('/api/import/members/execute', { method: 'POST', body: formData, isFormData: true });
      setMemberResult(data);
      setMemberPreview(null);
    } catch (err) { setMemberError(err.message); }
    setMemberImporting(false);
  };

  const downloadTemplate = () => {
    window.open(`/api/import/member-template?token=${token}`, '_blank');
  };

  const fmt = (n) => {
    if (!n) return '0,00';
    return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div>
      <div className="page-header">
        <h1>Importer data</h1>
      </div>

      <div className="tabs" style={{display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid var(--gray-200)'}}>
        <button
          onClick={() => setActiveTab('kassekladde')}
          style={{
            padding: '0.75rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: activeTab === 'kassekladde' ? 600 : 400, fontSize: '0.95rem', fontFamily: 'inherit',
            color: activeTab === 'kassekladde' ? 'var(--primary)' : 'var(--gray-500)',
            borderBottom: activeTab === 'kassekladde' ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: '-2px'
          }}>
          Kassekladde / regnskab
        </button>
        <button
          onClick={() => setActiveTab('members')}
          style={{
            padding: '0.75rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: activeTab === 'members' ? 600 : 400, fontSize: '0.95rem', fontFamily: 'inherit',
            color: activeTab === 'members' ? 'var(--primary)' : 'var(--gray-500)',
            borderBottom: activeTab === 'members' ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: '-2px'
          }}>
          Medlemsliste
        </button>
      </div>

      {/* =================== KASSEKLADDE TAB =================== */}
      {activeTab === 'kassekladde' && (
        <>
          <div className="card">
            <h3>Upload regneark</h3>
            <p style={{color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '1rem'}}>
              Upload en Excel-fil (.xlsx, .xls) eller CSV-fil med dit regnskab.
              Systemet genkender automatisk kolonner som Kasse, Bank, Kontingent, Banko m.fl.
            </p>
            <div className="file-upload" onClick={() => fileRef.current?.click()}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
              {file ? (
                <p><strong>{file.name}</strong> ({(file.size / 1024).toFixed(0)} KB)</p>
              ) : (
                <p>Klik her eller tr&aelig;k en fil hertil</p>
              )}
            </div>
            {file && !preview && !result && (
              <button className="btn btn-primary" onClick={handlePreview} disabled={loading} style={{marginTop: '1rem'}}>
                {loading ? 'L\u00e6ser fil...' : 'Forh\u00e5ndsvis data'}
              </button>
            )}
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {result && (
            <div className="alert alert-success">
              Import f&aelig;rdig! {result.imported} bilag importeret
              {result.skipped > 0 && `, ${result.skipped} r&aelig;kker sprunget over (ubalanceret eller mangler data)`}.
            </div>
          )}

          {preview && (
            <div className="card">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <h3>Forh&aring;ndsvisning ({preview.totalRows} r&aelig;kker fundet)</h3>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                  <button className="btn btn-secondary" onClick={() => { setPreview(null); setFile(null); }}>Annuller</button>
                  <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
                    {importing ? 'Importerer...' : `Importer ${preview.totalRows} r\u00e6kker`}
                  </button>
                </div>
              </div>
              {preview.columns.length > 0 && (
                <div style={{marginBottom: '1rem'}}>
                  <h4 style={{fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '0.5rem'}}>Genkendte kolonner</h4>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                    {preview.columns.map((col, i) => (
                      <span key={i} className="badge badge-active">
                        {col.label} &rarr; {col.account_number} ({col.side === 'debit' ? 'D' : 'K'})
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="table-container" style={{maxHeight: '500px', overflow: 'auto'}}>
                <table>
                  <thead>
                    <tr>
                      <th>R&aelig;kke</th>
                      <th>Dato</th>
                      <th>Beskrivelse</th>
                      <th>Posteringer</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.entries.slice(0, 100).map((entry, i) => {
                      const totalDebit = entry.lines.filter(l => l.side === 'debit').reduce((s, l) => s + l.amount, 0);
                      const totalCredit = entry.lines.filter(l => l.side === 'credit').reduce((s, l) => s + l.amount, 0);
                      const balanced = Math.abs(totalDebit - totalCredit) < 0.01;
                      return (
                        <tr key={i}>
                          <td>{entry.row}</td>
                          <td>{entry.date || '-'}</td>
                          <td>{entry.description || '-'}</td>
                          <td>
                            {entry.lines.map((l, j) => (
                              <div key={j} style={{fontSize: '0.8rem'}}>
                                {l.side === 'debit' ? 'D' : 'K'}: {l.account_number} = {fmt(l.amount)}
                                <span style={{color: 'var(--gray-500)', marginLeft: '0.25rem'}}>({l.label})</span>
                              </div>
                            ))}
                          </td>
                          <td>
                            {balanced ? (
                              <span className="badge badge-active">OK</span>
                            ) : entry.lines.length < 2 ? (
                              <span className="badge badge-inactive">For f&aring; linjer</span>
                            ) : (
                              <span className="badge badge-inactive">Ubalance: {fmt(Math.abs(totalDebit - totalCredit))}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {preview.entries.length > 100 && (
                <p style={{color: 'var(--gray-500)', fontSize: '0.8rem', marginTop: '0.5rem'}}>
                  Viser de f&oslash;rste 100 af {preview.entries.length} r&aelig;kker.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* =================== MEMBERS TAB =================== */}
      {activeTab === 'members' && (
        <>
          <div className="card">
            <h3>Importer medlemsliste</h3>
            <p style={{color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '1rem'}}>
              Upload en Excel-fil (.xlsx, .xls) eller CSV-fil med medlemmer.
              Kolonner genkendes automatisk ud fra overskrifter (Navn, Adresse, Telefon, Email osv.).
            </p>

            <div style={{marginBottom: '1rem'}}>
              <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}>
                Download Excel-skabelon
              </button>
              <span style={{color: 'var(--gray-500)', fontSize: '0.8rem', marginLeft: '0.75rem'}}>
                Brug skabelonen som udgangspunkt for din medlemsliste
              </span>
            </div>

            <div className="file-upload" onClick={() => memberFileRef.current?.click()}>
              <input ref={memberFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleMemberFileChange} />
              {memberFile ? (
                <p><strong>{memberFile.name}</strong> ({(memberFile.size / 1024).toFixed(0)} KB)</p>
              ) : (
                <p>Klik her eller tr&aelig;k en fil hertil</p>
              )}
            </div>

            {memberFile && !memberPreview && !memberResult && (
              <button className="btn btn-primary" onClick={handleMemberPreview} disabled={memberLoading} style={{marginTop: '1rem'}}>
                {memberLoading ? 'L\u00e6ser fil...' : 'Forh\u00e5ndsvis medlemmer'}
              </button>
            )}
          </div>

          {memberError && <div className="alert alert-error">{memberError}</div>}

          {memberResult && (
            <div className="alert alert-success">
              Import f&aelig;rdig! {memberResult.imported} medlemmer importeret
              {memberResult.skipped > 0 && `, ${memberResult.skipped} r&aelig;kker sprunget over (mangler navn)`}.
            </div>
          )}

          {memberPreview && (
            <div className="card">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <h3>Forh&aring;ndsvisning ({memberPreview.totalRows} medlemmer fundet)</h3>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                  <button className="btn btn-secondary" onClick={() => { setMemberPreview(null); setMemberFile(null); }}>Annuller</button>
                  <button className="btn btn-primary" onClick={handleMemberImport} disabled={memberImporting}>
                    {memberImporting ? 'Importerer...' : `Importer ${memberPreview.totalRows} medlemmer`}
                  </button>
                </div>
              </div>

              {memberPreview.columns.length > 0 && (
                <div style={{marginBottom: '1rem'}}>
                  <h4 style={{fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '0.5rem'}}>Genkendte kolonner</h4>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                    {memberPreview.columns.map((col, i) => (
                      <span key={i} className="badge badge-active">{col.label}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="table-container" style={{maxHeight: '500px', overflow: 'auto'}}>
                <table>
                  <thead>
                    <tr>
                      <th>Nr.</th>
                      <th>Fornavn(e)</th>
                      <th>Efternavn</th>
                      <th>Adresse</th>
                      <th>Postnr.</th>
                      <th>By</th>
                      <th>Telefon</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberPreview.members.slice(0, 100).map((m, i) => (
                      <tr key={i}>
                        <td>{m.member_number || '-'}</td>
                        <td><strong>{m.name}</strong></td>
                        <td>{m.last_name || '-'}</td>
                        <td>{m.address || '-'}</td>
                        <td>{m.postal_code || '-'}</td>
                        <td>{m.city || '-'}</td>
                        <td>{m.phone || '-'}</td>
                        <td>{m.email || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {memberPreview.members.length > 100 && (
                <p style={{color: 'var(--gray-500)', fontSize: '0.8rem', marginTop: '0.5rem'}}>
                  Viser de f&oslash;rste 100 af {memberPreview.members.length} medlemmer.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
